/**
 * Organization and contact contracts. Destination: `@renote/contracts`.
 *
 * These are the ERP's party records, not CRM-private ones — POS and field
 * service are expected to reference them later, so the shapes stay generic.
 */

import { z } from 'zod'
import {
  arrayParam,
  boolParam,
  jsonParam,
  viewFilter,
  currencyCode,
  instant,
  optionalEmail,
  optionalUrl,
  pageOf,
  pageQuery,
  sortDirection,
  trimmedText,
  uuid,
} from './common'

const address = {
  addressLine1: z.string().nullable(),
  addressLine2: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
}

export const organization = z.object({
  id: uuid,
  name: z.string(),
  website: z.string().nullable(),
  industryId: uuid.nullable(),
  noOfEmployees: z.number().int().nonnegative().nullable(),
  annualRevenue: z.number().nonnegative().nullable(),
  currency: z.string().nullable(),
  ...address,
  ownerId: uuid.nullable(),
  archivedAt: instant.nullable(),
  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
})
export type Organization = z.infer<typeof organization>

export const createOrganizationInput = z
  .object({
    name: z.string().trim().min(1).max(200),
    website: optionalUrl,
    industryId: uuid.nullable(),
    noOfEmployees: z.number().int().nonnegative().nullable(),
    annualRevenue: z.number().nonnegative().nullable(),
    currency: currencyCode.nullable(),
    addressLine1: trimmedText(200),
    addressLine2: trimmedText(200),
    city: trimmedText(100),
    state: trimmedText(100),
    postalCode: trimmedText(20),
    country: trimmedText(100),
    ownerId: uuid.nullable(),
  })
  .strict()
  // mirrors organization_revenue_has_currency
  .refine((v) => v.annualRevenue === null || v.currency !== null, {
    message: 'Currency is required when revenue is set',
    path: ['currency'],
  })
export type CreateOrganizationInput = z.infer<typeof createOrganizationInput>

export const updateOrganizationInput = createOrganizationInput
  .innerType()
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
  /**
   * Mirrors organization_revenue_has_currency. Only judged when the payload
   * carries both fields; a patch touching one of them cannot be evaluated
   * without the stored row, so the database stays the backstop.
   */
  .refine(
    (v) =>
      !('annualRevenue' in v && 'currency' in v) || v.annualRevenue === null || v.currency !== null,
    { message: 'Currency is required when revenue is set', path: ['currency'] },
  )
export type UpdateOrganizationInput = z.infer<typeof updateOrganizationInput>

export const organizationSortField = z.enum(['name', 'createdAt', 'updatedAt', 'annualRevenue'])

export const organizationListQuery = pageQuery
  .extend({
    search: z.string().trim().max(200).optional(),
    industryId: arrayParam(uuid).optional(),
    ownerId: arrayParam(uuid).optional(),
    /**
     * Ad-hoc filters from the view builder, validated against the entity's field
     * registry server-side before compilation. The named facets above stay for
     * the common cases and for hand-written links.
     */
    filters: jsonParam(z.array(viewFilter)).optional(),
    includeArchived: boolParam.default(false),
    sort: organizationSortField.default('updatedAt'),
    direction: sortDirection.default('desc'),
  })
  .strict()
export type OrganizationListQuery = z.infer<typeof organizationListQuery>

export const organizationListResult = pageOf(organization)
export type OrganizationListResult = z.infer<typeof organizationListResult>


export const contact = z.object({
  id: uuid,
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: z.string().nullable(),
  phone: z.string().nullable(),
  mobile: z.string().nullable(),
  jobTitle: z.string().nullable(),
  organizationId: uuid.nullable(),
  ownerId: uuid.nullable(),
  archivedAt: instant.nullable(),
  createdBy: uuid,
  createdAt: instant,
  updatedAt: instant,
})
export type Contact = z.infer<typeof contact>

export const createContactInput = z
  .object({
    firstName: trimmedText(140),
    lastName: trimmedText(140),
    email: optionalEmail,
    phone: trimmedText(40),
    mobile: trimmedText(40),
    jobTitle: trimmedText(140),
    organizationId: uuid.nullable(),
    ownerId: uuid.nullable(),
  })
  .strict()
  // mirrors contact_has_a_name
  .refine(
    (v) => [v.firstName, v.lastName, v.email].some((field) => field !== null),
    { message: 'Provide at least a name or an email', path: ['firstName'] },
  )
export type CreateContactInput = z.infer<typeof createContactInput>

const CONTACT_IDENTITY_KEYS = ['firstName', 'lastName', 'email'] as const

export const updateContactInput = createContactInput
  .innerType()
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
  /** Mirrors contact_has_a_name; see updateOrganizationInput for the reasoning. */
  .refine(
    (v) => {
      const complete = CONTACT_IDENTITY_KEYS.every((key) => key in v)
      if (!complete) return true
      return CONTACT_IDENTITY_KEYS.some((key) => v[key] !== null)
    },
    { message: 'Provide at least a name or an email', path: ['firstName'] },
  )
export type UpdateContactInput = z.infer<typeof updateContactInput>

export const contactSortField = z.enum(['lastName', 'firstName', 'createdAt', 'updatedAt'])

export const contactListQuery = pageQuery
  .extend({
    search: z.string().trim().max(200).optional(),
    organizationId: arrayParam(uuid).optional(),
    ownerId: arrayParam(uuid).optional(),
    /**
     * Ad-hoc filters from the view builder, validated against the entity's field
     * registry server-side before compilation. The named facets above stay for
     * the common cases and for hand-written links.
     */
    filters: jsonParam(z.array(viewFilter)).optional(),
    includeArchived: boolParam.default(false),
    sort: contactSortField.default('updatedAt'),
    direction: sortDirection.default('desc'),
  })
  .strict()
export type ContactListQuery = z.infer<typeof contactListQuery>

export const contactListResult = pageOf(contact)
export type ContactListResult = z.infer<typeof contactListResult>
